/**
 * Space Invaders Arcade Engine - Pink Pixel OS
 * Full game state, pixel collision, bunker degradation, armada marching logic.
 */

export const WORLD_WIDTH = 480
export const WORLD_HEIGHT = 600

// 8x8 & 11x8 Invader pixel matrices (1 = filled pixel, 0 = empty)
export const SPRITES = {
  // Squid (Row 0, 30pts) - 8x8
  squid: [
    [
      '00011000',
      '00111100',
      '01111110',
      '11011011',
      '11111111',
      '00100100',
      '01011010',
      '10100101'
    ],
    [
      '00011000',
      '00111100',
      '01111110',
      '11011011',
      '11111111',
      '01011010',
      '10000001',
      '01000010'
    ]
  ],
  // Crab (Rows 1 & 2, 20pts) - 11x8
  crab: [
    [
      '00100000100',
      '00010001000',
      '00111111100',
      '01101110110',
      '11111111111',
      '10111111101',
      '10100000101',
      '00011011000'
    ],
    [
      '00100000100',
      '10010001001',
      '10111111101',
      '11101110111',
      '11111111111',
      '01111111110',
      '00100000100',
      '01000000010'
    ]
  ],
  // Octopus (Rows 3 & 4, 10pts) - 12x8
  octopus: [
    [
      '000011110000',
      '011111111110',
      '111111111111',
      '111001100111',
      '111111111111',
      '000110011000',
      '001101101100',
      '110000000011'
    ],
    [
      '000011110000',
      '011111111110',
      '111111111111',
      '111001100111',
      '111111111111',
      '001101101100',
      '010000000010',
      '001000000100'
    ]
  ],
  // Mystery UFO Saucer (16x7)
  ufo: [
    '0000011111100000',
    '0001111111111000',
    '0011111111111100',
    '0110110110110110',
    '1111111111111111',
    '0011100110011100',
    '0001000000001000'
  ],
  // Player Cannon (13x8)
  cannon: [
    '0000001000000',
    '0000011100000',
    '0000011100000',
    '0111111111110',
    '1111111111111',
    '1111111111111',
    '1111111111111',
    '1111111111111'
  ]
}

// Build 4 classic destructible bunker shields
export function createShields() {
  const shields = []
  const shieldWidth = 44
  const shieldHeight = 32
  const shieldY = WORLD_HEIGHT - 130
  const spacing = (WORLD_WIDTH - shieldWidth * 4) / 5

  for (let i = 0; i < 4; i++) {
    const startX = spacing * (i + 1) + shieldWidth * i
    const blocks = []
    const cols = 11
    const rows = 8
    const blockW = shieldWidth / cols
    const blockH = shieldHeight / rows

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        // Cut out top corners and bottom archway
        if ((r === 0 && (c === 0 || c === cols - 1)) ||
            (r >= 5 && c >= 3 && c <= 7)) {
          continue
        }
        blocks.push({
          x: startX + c * blockW,
          y: shieldY + r * blockH,
          w: blockW,
          h: blockH,
          alive: true
        })
      }
    }
    shields.push({ x: startX, blocks })
  }
  return shields
}

export function createInitialInvaders(wave = 1) {
  const invaders = []
  const startY = Math.min(80 + (wave - 1) * 15, 160)
  const rows = 5
  const cols = 11
  const xSpacing = 36
  const ySpacing = 28
  const startX = (WORLD_WIDTH - (cols - 1) * xSpacing) / 2

  for (let r = 0; r < rows; r++) {
    let type = 'octopus'
    let score = 10
    let width = 24
    let height = 16
    let color = '#38bdf8' // sky blue

    if (r === 0) {
      type = 'squid'
      score = 30
      width = 18
      height = 16
      color = '#ec4899' // pink
    } else if (r === 1 || r === 2) {
      type = 'crab'
      score = 20
      width = 22
      height = 16
      color = '#34d399' // emerald green
    }

    for (let c = 0; c < cols; c++) {
      invaders.push({
        id: `${r}-${c}`,
        row: r,
        col: c,
        x: startX + c * xSpacing,
        y: startY + r * ySpacing,
        w: width,
        h: height,
        type,
        score,
        color,
        alive: true
      })
    }
  }
  return invaders
}

export function createInitialGameState() {
  const savedHigh = typeof localStorage !== 'undefined' ? parseInt(localStorage.getItem('pp_spaceinvaders_highscore') || '0', 10) : 0
  return {
    player: {
      x: WORLD_WIDTH / 2 - 13,
      y: WORLD_HEIGHT - 55,
      w: 26,
      h: 16,
      speed: 210,
      alive: true
    },
    lives: 3,
    score: 0,
    highScore: savedHigh,
    wave: 1,
    state: 'idle', // 'idle' | 'playing' | 'paused' | 'player_dying' | 'game_over' | 'wave_cleared'
    invaders: createInitialInvaders(1),
    shields: createShields(),
    playerLaser: null, // { x, y, vy: -480, w: 3, h: 10 }
    alienLasers: [], // [{ x, y, vy: 240, w: 3, h: 8 }]
    ufo: null, // { x, y: 45, vx: 120, w: 32, h: 14, score: 100 }
    ufoTimer: 18,
    invaderDir: 1, // 1 for right, -1 for left
    invaderStepTimer: 0,
    animFrame: 0,
    playerDeathTimer: 0,
    waveTransitionTimer: 0
  }
}

export function getStepInterval(aliveCount) {
  // Speed scales from ~0.75s down to 0.05s as invaders perish
  const ratio = aliveCount / 55
  return Math.max(0.06, 0.06 + ratio * 0.69)
}

export function updateGame(game, dt, input, audio) {
  if (game.state !== 'playing' && game.state !== 'player_dying' && game.state !== 'wave_cleared') {
    return game
  }

  // Handle Wave Cleared Transition
  if (game.state === 'wave_cleared') {
    game.waveTransitionTimer -= dt
    if (game.waveTransitionTimer <= 0) {
      game.wave += 1
      game.invaders = createInitialInvaders(game.wave)
      game.shields = createShields()
      game.playerLaser = null
      game.alienLasers = []
      game.invaderDir = 1
      game.state = 'playing'
    }
    return game
  }

  // Handle Player Death Delay
  if (game.state === 'player_dying') {
    game.playerDeathTimer -= dt
    if (game.playerDeathTimer <= 0) {
      if (game.lives <= 0) {
        game.state = 'game_over'
        if (game.score > game.highScore) {
          game.highScore = game.score
          try { localStorage.setItem('pp_spaceinvaders_highscore', String(game.score)) } catch {}
        }
      } else {
        game.player.alive = true
        game.player.x = WORLD_WIDTH / 2 - 13
        game.playerLaser = null
        game.alienLasers = []
        game.state = 'playing'
      }
    }
    return game
  }

  // --- 1. Player Movement & Firing ---
  if (input.left) {
    game.player.x = Math.max(16, game.player.x - game.player.speed * dt)
  }
  if (input.right) {
    game.player.x = Math.min(WORLD_WIDTH - 16 - game.player.w, game.player.x + game.player.speed * dt)
  }
  if (input.shoot && !game.playerLaser && game.player.alive) {
    game.playerLaser = {
      x: game.player.x + game.player.w / 2 - 1.5,
      y: game.player.y - 6,
      w: 3,
      h: 12,
      vy: -480
    }
    audio?.playShoot()
  }

  // --- 2. Update Player Laser ---
  if (game.playerLaser) {
    game.playerLaser.y += game.playerLaser.vy * dt
    if (game.playerLaser.y < 25) {
      game.playerLaser = null
    }
  }

  // --- 3. Invaders Marching ---
  const aliveInvaders = game.invaders.filter(inv => inv.alive)
  if (aliveInvaders.length === 0) {
    game.state = 'wave_cleared'
    game.waveTransitionTimer = 1.5
    return game
  }

  const stepInterval = getStepInterval(aliveInvaders.length)
  game.invaderStepTimer += dt

  if (game.invaderStepTimer >= stepInterval) {
    game.invaderStepTimer = 0
    game.animFrame = game.animFrame === 0 ? 1 : 0
    audio?.playMarchStep()

    // Check boundary collision
    let hitEdge = false
    for (const inv of aliveInvaders) {
      if ((game.invaderDir === 1 && inv.x + inv.w >= WORLD_WIDTH - 18) ||
          (game.invaderDir === -1 && inv.x <= 18)) {
        hitEdge = true
        break
      }
    }

    if (hitEdge) {
      game.invaderDir = -game.invaderDir
      for (const inv of aliveInvaders) {
        inv.y += 18
      }
    } else {
      const stepDist = 10
      for (const inv of aliveInvaders) {
        inv.x += game.invaderDir * stepDist
      }
    }

    // Check if invaders invaded player base
    for (const inv of aliveInvaders) {
      if (inv.y + inv.h >= game.player.y) {
        game.lives = 0
        game.state = 'game_over'
        audio?.playPlayerExplosion()
        return game
      }
    }
  }

  // --- 4. Alien Laser Firing ---
  // Aliens drop lasers with probability
  if (game.alienLasers.length < 3 && Math.random() < 0.04) {
    // Pick a random alive bottom-most invader
    const columns = new Map()
    for (const inv of aliveInvaders) {
      if (!columns.has(inv.col) || columns.get(inv.col).y < inv.y) {
        columns.set(inv.col, inv)
      }
    }
    const shooters = Array.from(columns.values())
    if (shooters.length > 0) {
      const shooter = shooters[Math.floor(Math.random() * shooters.length)]
      game.alienLasers.push({
        x: shooter.x + shooter.w / 2 - 1.5,
        y: shooter.y + shooter.h + 2,
        w: 3,
        h: 10,
        vy: 230 + game.wave * 15
      })
    }
  }

  // Update Alien Lasers
  for (let i = game.alienLasers.length - 1; i >= 0; i--) {
    const laser = game.alienLasers[i]
    laser.y += laser.vy * dt
    if (laser.y > WORLD_HEIGHT) {
      game.alienLasers.splice(i, 1)
      continue
    }

    // Hit Player?
    if (
      game.player.alive &&
      laser.x < game.player.x + game.player.w &&
      laser.x + laser.w > game.player.x &&
      laser.y < game.player.y + game.player.h &&
      laser.y + laser.h > game.player.y
    ) {
      game.alienLasers.splice(i, 1)
      game.lives -= 1
      game.player.alive = false
      game.state = 'player_dying'
      game.playerDeathTimer = 1.2
      audio?.playPlayerExplosion()
      break
    }
  }

  // --- 5. Mystery UFO ---
  if (!game.ufo) {
    game.ufoTimer -= dt
    if (game.ufoTimer <= 0) {
      const dir = Math.random() < 0.5 ? 1 : -1
      game.ufo = {
        x: dir === 1 ? -35 : WORLD_WIDTH + 5,
        y: 45,
        vx: dir * 110,
        w: 32,
        h: 14,
        score: [50, 100, 150, 300][Math.floor(Math.random() * 4)]
      }
      audio?.startUfoSound()
      game.ufoTimer = 22 + Math.random() * 15
    }
  } else {
    game.ufo.x += game.ufo.vx * dt
    if (game.ufo.x < -40 || game.ufo.x > WORLD_WIDTH + 40) {
      game.ufo = null
      audio?.stopUfoSound()
    }
  }

  // --- 6. Shield Collisions ---
  const checkShieldHit = (laser) => {
    for (const shield of game.shields) {
      for (const block of shield.blocks) {
        if (
          block.alive &&
          laser.x < block.x + block.w &&
          laser.x + laser.w > block.x &&
          laser.y < block.y + block.h &&
          laser.y + laser.h > block.y
        ) {
          block.alive = false
          return true
        }
      }
    }
    return false
  }

  // Check player laser against shields
  if (game.playerLaser && checkShieldHit(game.playerLaser)) {
    game.playerLaser = null
  }

  // Check alien lasers against shields
  for (let i = game.alienLasers.length - 1; i >= 0; i--) {
    if (checkShieldHit(game.alienLasers[i])) {
      game.alienLasers.splice(i, 1)
    }
  }

  // --- 7. Player Laser vs Invaders & UFO ---
  if (game.playerLaser) {
    // Check UFO hit
    if (
      game.ufo &&
      game.playerLaser.x < game.ufo.x + game.ufo.w &&
      game.playerLaser.x + game.playerLaser.w > game.ufo.x &&
      game.playerLaser.y < game.ufo.y + game.ufo.h &&
      game.playerLaser.y + game.playerLaser.h > game.ufo.y
    ) {
      game.score += game.ufo.score
      game.playerLaser = null
      game.ufo = null
      audio?.stopUfoSound()
      audio?.playInvaderKilled()
    } else {
      // Check Invader hit
      for (const inv of aliveInvaders) {
        if (
          game.playerLaser.x < inv.x + inv.w &&
          game.playerLaser.x + game.playerLaser.w > inv.x &&
          game.playerLaser.y < inv.y + inv.h &&
          game.playerLaser.y + game.playerLaser.h > inv.y
        ) {
          inv.alive = false
          game.score += inv.score
          game.playerLaser = null
          audio?.playInvaderKilled()
          break
        }
      }
    }
  }

  // Update high score in real time
  if (game.score > game.highScore) {
    game.highScore = game.score
  }

  return game
}
